// Utilities for printing out XML-like fragments into AI prompts.
//
// Perhaps, we could leverage an existing XML-handling library,
// but on the other hand, our goal here is not to operate a full-feature
// XML stuff, but to conveniently compose AI prompts with XML-like syntax,
// thus let's try to code from scratch what looks more convenient for that.

/**
 * Just a little string wrapper; its `kind` field tells what payload is stored
 * in this string, which adds some degree of type safety as we pass such strings
 * all around.
 */
export type XmlNodeT<kind extends string> = {
  kind: kind;
  xml: string;
};

export type XmlAnyNodeT = XmlNodeT<string>;
export type XmlNodeArrayT = Array<XmlAnyNodeT | null>;

// Regular expressions for very simple validations of xmlNode() arguments.

const REGEX_ATTR_NAME = /^[a-z_-]+$/;
const REGEX_ATTR_VALUE = /^[^"]*$/;
const REGEX_CONTENT_STRING = /^[^<>]*$/;
const REGEX_TAG = /^[a-z_-]+$/;

export function xmlNode<Kind extends string>(
  tag: Kind,
  content: XmlAnyNodeT | XmlNodeArrayT | string,
  descriptionOrAttributes?: Record<string, string> | string,
): XmlNodeT<Kind>;

export function xmlNode<Kind extends string>(
  tag: Kind,
  content: XmlAnyNodeT | XmlNodeArrayT | null | string | undefined,
  descriptionOrAttributes?: Record<string, string> | string,
): XmlNodeT<Kind> | null;

/**
 * Prints out requested XML node, with a minimal validation of given arguments.
 *
 * @param tag - XML node's tag.
 *
 * @param content - XML node's content. If "null" or "undefined", this function
 *  returns "null". If a string is provided, it is validated to not contain "<"
 *  and ">" characters; otherwise, the given content node(s) are printed out as
 *  the node's content without any additional validation.
 *
 * @param descriptionOrAttributes - Optional. If a string is provided, it is
 *  used as the value of the "description" attribute of this XML node; if an
 *  object is provided, it is treated as a map of attribute name/value pairs,
 *  to be added to this XML node.
 */
export function xmlNode<Kind extends string>(
  tag: Kind,
  content: XmlAnyNodeT | XmlNodeArrayT | null | string | undefined,
  descriptionOrAttributes?: Record<string, string> | string,
): XmlNodeT<Kind> | null {
  if (content === null || content === undefined) return null;

  if (!tag.match(REGEX_TAG)) throw Error(`Invalid XML tag: ${tag}`);

  let attributeString = '';
  if (descriptionOrAttributes) {
    if (typeof descriptionOrAttributes === 'string') {
      const escaped = descriptionOrAttributes.replace(/"/g, '&quot;');
      attributeString += ` description="${escaped}"`;
    } else {
      for (const [name, value] of Object.entries(descriptionOrAttributes)) {
        if (!name.match(REGEX_ATTR_NAME)) {
          throw Error(`Invalid XML attribute name: ${name}`);
        }

        if (!value.match(REGEX_ATTR_VALUE)) {
          throw Error(`Invalid XML attribute value: ${value}`);
        }

        attributeString += ` ${name}="${value}"`;
      }
    }
  }

  let contentString: string;
  if (Array.isArray(content)) {
    contentString = '';
    for (const node of content) {
      if (node) {
        if (contentString) contentString += '\n';
        contentString += node.xml;
      }
    }
  } else if (typeof content === 'string') {
    if (!content.match(REGEX_CONTENT_STRING)) {
      throw Error(`Invalid XML content string: ${content}`);
    }

    contentString = content;
  } else contentString = content.xml;

  return {
    kind: tag,
    xml: contentString.trim() === ''
      ? `<${tag}${attributeString} />`
      : `<${tag}${attributeString}>\n${contentString}\n</${tag}>`,
  };
}
